import * as React from 'react'
import { FunctionComponent, useContext, useMemo } from 'react'
import { Button, makeStyles } from '@fluentui/react-components'
import { mergeStyles } from '@fluentui/react/lib/Styling'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import { AppAction } from '../../config/appConfig'
import { IAppState } from '../../reducers/appReducer'
import { useBoolean, useMount } from '@fluentui/react-hooks'
import useHost from '../../hooks/useHost'
import IdentityContext from '../../context/IdentityContext'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { DirectionalHint, getTheme, Icon, mergeStyleSets, Stack, TeachingBubble, Text } from '@fluentui/react'
import PersonAvatar from '../PersonAvatar'
import { useApplicationStyles } from '../../pages/App'
import { HammockIcon } from '../Icon/HammockIcon'

export type Build = {
    name: string
    version: string
    timestamp: bigint
    author: string
}

const theme = getTheme()

const classNames = mergeStyleSets({
    whiteText: mergeStyles({ color: theme.palette.white }),
    lightText: mergeStyles({ color: theme.palette.themeLighterAlt }),
})

const useStyles = makeStyles({
    alignSelfCenter: {
        alignSelf: 'center',
    },
})

type BubbleProps = { toggleBubble: () => void }

const Bubble: FunctionComponent<BubbleProps> = props => {
    const { t: translate } = useTranslation()
    const version = (process.env.BUILD as unknown as Build).version

    return (
        <TeachingBubble
            target="#versionNumber"
            calloutProps={{
                directionalHint: DirectionalHint.topRightEdge,
            }}
            primaryButtonProps={{
                text: translate('release.notes'),
                iconProps: { iconName: 'MSNVideosSolid' },
                onClick: () => {
                    props.toggleBubble()
                    window.open(process.env.RELEASE_NOTES_URL, '_blank')
                },
            }}
            secondaryButtonProps={{
                text: translate('action.close'),
                iconProps: { iconName: 'Completed', className: classNames.lightText },
                onClick: () => props.toggleBubble(),
            }}
            headline={translate('release.title')}
        >
            <Stack tokens={{ childrenGap: 's2' }}>
                <Text block className={classNames.lightText}>
                    {translate('release.version', { version: version })}{' '}
                    <Icon iconName="GiftboxOpen" className={classNames.whiteText} />.
                </Text>
                <Text block className={classNames.lightText}>
                    {translate('release.view')}
                </Text>
            </Stack>
        </TeachingBubble>
    )
}

const Version: FunctionComponent = () => {
    const applicationStyles = useApplicationStyles()
    const dispatch = useDispatch()
    const host = useHost()
    const styles = useStyles()
    const version = (process.env.BUILD as unknown as Build).version
    const { viewport, isViewport } = useViewport()

    const app = useSelector<Store, IAppState>(state => state.app)
    const { authenticated } = useContext(IdentityContext)
    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])
    const [showBubble, { toggle: toggleBubble }] = useBoolean(false)

    useMount(() => {
        if (!app.version || app.version !== version) {
            // Log the app out so that we know we sure that the app state is renewed.
            dispatch({ type: AppAction.SetVersion, payload: version })
        }
    })

    return (
        <>
            {smallMenu && (
                <PersonAvatar
                    name={`${host?.Name ?? ''} v${version}`}
                    className={styles.alignSelfCenter}
                    color={'colorful'}
                    size={24}
                />
            )}
            <Button
                id={'versionNumber'}
                icon={<HammockIcon />}
                appearance={'subtle'}
                iconPosition={'after'}
                onClick={toggleBubble}
                className={applicationStyles.thinTextButton}
            >
                {!smallMenu && `${host?.Name ?? ''} v${version}`}
            </Button>
            {authenticated && showBubble && <Bubble toggleBubble={toggleBubble} />}
        </>
    )
}

export default Version
